import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import { SquareArrowOutUpRight } from "lucide-react";
import { Link } from "react-router";

import { cn } from "~/utils/cn";

interface ChatArtifactCardProps {
  name: string;
  // Short type badge, e.g. "HTML", "PDF", "MD".
  kind: string;
  // Secondary line, e.g. "v14 · 184 KB".
  meta?: string;
  // Opens the artifact in the page's canvas pane.
  onOpen?: () => void;
  // Falls back to a link to the resource in the library when there is no canvas.
  href?: string;
  className?: string;
}

// The artifact attachment card shown under an assistant turn (e.g. a generated
// "Readme.html"): a type badge, the file name + metadata, and an open control that
// routes to the canvas pane (or the library when no canvas is present).
export function ChatArtifactCard({ name, kind, meta, onOpen, href, className }: ChatArtifactCardProps) {
  const openButton = onOpen ? (
    <Button variant="ghost" size="icon-sm" onClick={onOpen} aria-label={`Open ${name}`}>
      <SquareArrowOutUpRight className="size-4" />
    </Button>
  ) : href ? (
    <Button variant="ghost" size="icon-sm" asChild aria-label={`Open ${name}`}>
      <Link to={href}>
        <SquareArrowOutUpRight className="size-4" />
      </Link>
    </Button>
  ) : null;

  return (
    <div className={cn("border-border bg-card flex max-w-full items-center gap-3 rounded-lg border p-2.5", className)}>
      <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold tracking-wide uppercase">
        {kind}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-foreground truncate text-sm font-medium">{name}</div>
        {meta ? <div className="text-muted-foreground truncate text-xs">{meta}</div> : null}
      </div>
      {openButton ? (
        <Tooltip>
          <TooltipTrigger asChild>{openButton}</TooltipTrigger>
          <TooltipContent side="top">Open</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}
